import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { Check, Copy, PencilSimple } from 'phosphor-react';
import { toast } from 'sonner';
import { IEstatutoFormPayload } from '../../../../hooks/useEstatuto';
import { ICartorio } from '../../../../hooks/useCartorios';
import { formatDateBR, formatDateTimeBR } from '../utils';
import { formatarCpf } from '../../../estatutoPublico/PessoasField';
import { EstatutoCardActions } from '../hooks/useEstatutoCardActions';
import { EstatutoAssinaturaStatus } from './EstatutoAssinaturaStatus';
import { EstatutoBaixaDados } from './EstatutoBaixaDados';
import { EstatutoCorrigirPessoa } from './EstatutoCorrigirPessoa';
import { CartorioSelect } from './CartorioSelect';
import { EmpresaSelect, IEmpresaLite } from './EmpresaSelect';
import { ClienteSelect, IClienteLite } from './ClienteSelect';

const FIELD_LABELS: Partial<Record<keyof IEstatutoFormPayload, string>> = {
  numero_turma: 'Número da turma',
  nome_curso: 'Nome do curso',
  nome_instituicao: 'Instituição',
  semestre_formatura: 'Semestre de formatura',
  endereco_sede: 'Endereço da sede',
  vigencia_inicio: 'Vigência — início',
  vigencia_fim: 'Vigência — fim',
  minimo_cargos_diretoria: 'Mínimo de cargos da diretoria',
  reunioes_consecutivas_abandono: 'Reuniões consecutivas (abandono)',
  reunioes_alternadas_abandono: 'Reuniões alternadas (abandono)',
  periodo_reunioes_alternadas: 'Período das reuniões alternadas',
  periodicidade_balancete: 'Balancete do tesoureiro',
  balancete_trimestral_ate: 'Balancete trimestral até',
  periodicidade_balanco: 'Balanço contábil apresentado (Art. 16)',
  periodicidade_aprovacao_contas: 'Aprovação das contas (Art. 30)',
  prazo_contestacao_dias: 'Prazo de contestação (dias)',
  limite_assembleias_extraordinarias: 'Limite de assembleias extraordinárias',
  prazo_devolucao_dias: 'Devolução no afastamento (dias, Art. 36)',
  prazo_devolucao_desistencia_dias: 'Devolução na desistência (dias, Art. 37)',
  ano_limite_admissao: 'Novos associados até o 1º período letivo de',
  prazo_limite_migracao_plano_anos: 'Prazo limite de migração (anos)',
  canal_comunicacao: 'Canal de comunicação',
  eventos_formatura: 'Eventos de formatura',
  confirma_inexistencia_orgaos: 'Confirma inexistência de órgãos',
  signatario_nome: 'Nome do signatário',
  signatario_email: 'Email do signatário',
  signatario_cpf: 'CPF do signatário',
  data_assembleia: 'Data da assembleia de fundação (o Estatuto sai 5 dias antes)',
  data_requerimento: 'Data do requerimento',
  cidade_assinatura: 'Cidade/UF de assinatura',
  votos_favor: 'Votos a favor do estatuto',
  votos_contra: 'Votos contra o estatuto',
  total_eleitores: 'Total de eleitores (diretoria)',
  presidente_trabalhos_cpf: 'Presidente dos trabalhos',
  secretario_trabalhos_cpf: 'Secretário(a) dos trabalhos',
  pessoas: 'Fundadores (cargo; ausente = fora da lista de presença)',
};

const renderValue = (
  key: keyof IEstatutoFormPayload,
  value: IEstatutoFormPayload[keyof IEstatutoFormPayload],
  dados: IEstatutoFormPayload,
): string => {
  if (value == null || value === '') return '—';
  if (key === 'pessoas' && Array.isArray(value)) {
    return dados.pessoas
      .map(
        (p, i) =>
          `${i + 1}. ${p.nome} (${p.sexo}) · CPF ${formatarCpf(p.cpf)}${p.cargo ? ` · ${p.cargo}` : ''}${
            p.presente ? '' : ' · ausente'
          }`,
      )
      .join('\n');
  }
  if (key === 'presidente_trabalhos_cpf' || key === 'secretario_trabalhos_cpf') {
    return dados.pessoas?.find(p => p.cpf === value)?.nome ?? String(value);
  }
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  if (
    key === 'vigencia_inicio' ||
    key === 'vigencia_fim' ||
    key === 'data_assembleia' ||
    key === 'data_requerimento'
  ) {
    return formatDateBR(String(value));
  }
  return String(value);
};

interface EstatutoDadosTabProps {
  actions: EstatutoCardActions;
  // Título da coluna atual do card — o link público só aparece quando o card está na
  // coluna "Aguardando Validação do Cliente" (#16: o formulário é da etapa do cliente).
  columnTitle?: string | null;
}

// Coluna em que o formulário de preenchimento fica disponível ao cliente (#16).
const COLUNA_CLIENTE = 'Aguardando Validação do Cliente';

/**
 * Conteúdo rico do estatuto (portado do antigo DrawerBody): link público, datas,
 * "aguardando cliente" + motivo, cartório, advogado, responsáveis/supervisores e
 * os 22 campos preenchidos pelo cliente. Renderizado na aba extra "Estatuto" do
 * modal genérico. Inclui os modais filhos (assinatura / PDF), controlados pelas
 * disclosures do hook.
 */
export const EstatutoDadosTab: React.FC<EstatutoDadosTabProps> = ({ actions, columnTitle }) => {
  const {
    estatuto,
    isLoading,
    dados,
    publicUrl,
    linkCopied,
    aguardando,
    setAguardando,
    motivo,
    setMotivo,
    aguardandoSemMudanca,
    aguardandoMutation,
    editarMutation,
    handleCopyLink,
  } = actions;

  // Baixa do fluxo de 21/10 (com origem ou com as partes novas do formulário).
  const baixaNova =
    estatuto?.destino === 'baixa' &&
    !!dados &&
    ('convocacao' in (dados as any) || 'origem' in (dados as any));

  // Alteração (26/10): sem formulário, link nem resposta do cliente pela Área VIP.
  const alteracao = estatuto?.destino === 'alteracao';

  // #16: o link só aparece quando o card está na coluna do cliente (etapa do formulário).
  const mostrarLink = columnTitle === COLUNA_CLIENTE;

  // #14: edição dos dados do processo (advogado + cartório). Estado local dos inputs,
  // sincronizado com o estatuto ao (re)carregar; salva via editarMutation (PATCH).
  const [editando, setEditando] = React.useState(false);
  const [advNome, setAdvNome] = React.useState('');
  const [advEmail, setAdvEmail] = React.useState('');
  const [advCpf, setAdvCpf] = React.useState('');
  const [advOab, setAdvOab] = React.useState('');
  const [emailPresidente, setEmailPresidente] = React.useState('');
  const [cartorio, setCartorio] = React.useState<ICartorio | null>(null);
  const [empresa, setEmpresa] = React.useState<IEmpresaLite | null>(null);
  const [cliente, setCliente] = React.useState<IClienteLite | null>(null);

  React.useEffect(() => {
    setAdvNome(estatuto?.advogado_nome ?? '');
    setAdvEmail(estatuto?.advogado_email ?? '');
    setAdvCpf(estatuto?.advogado_cpf ?? '');
    setAdvOab(estatuto?.advogado_oab ?? '');
    setEmailPresidente(estatuto?.email_presidente ?? '');
    setCartorio(
      estatuto?.cartorio
        ? ({ id: estatuto.cartorio.id, nome: estatuto.cartorio.nome } as ICartorio)
        : null,
    );
    // Inicializa o seletor com o NOME resolvido pelo backend (empresa_nome); só cai no
    // placeholder "Empresa #<id>" se o backend não resolver (registro removido). Card SEM
    // empresa (empresa_id null) começa vazio — permite VINCULAR uma empresa (#14).
    setEmpresa(
      estatuto?.empresa_id != null
        ? {
            id: estatuto.empresa_id,
            razao_social: estatuto.empresa_nome || `Empresa #${estatuto.empresa_id}`,
          }
        : null,
    );
    // Igual à empresa: usa o cliente_nome resolvido pelo backend; placeholder só como
    // fallback. Assim o card reaberto já mostra o nome, sem depender de reabrir o seletor. (#25)
    setCliente(
      estatuto?.cliente_id != null
        ? {
            id: estatuto.cliente_id,
            nome: estatuto.cliente_nome || `Cliente #${estatuto.cliente_id}`,
          }
        : null,
    );
    setEditando(false);
  }, [
    estatuto?.id,
    estatuto?.advogado_nome,
    estatuto?.advogado_email,
    estatuto?.advogado_cpf,
    estatuto?.advogado_oab,
    estatuto?.email_presidente,
    estatuto?.cartorio,
    estatuto?.empresa_id,
    estatuto?.empresa_nome,
    estatuto?.cliente_id,
    estatuto?.cliente_nome,
  ]);

  const salvarEdicao = () => {
    if (emailPresidente.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailPresidente.trim())) {
      toast.error('E-mail do presidente inválido');
      return;
    }
    editarMutation.mutate({
      advogado_nome: advNome.trim() || null,
      advogado_email: advEmail.trim() || null,
      advogado_cpf: advCpf.trim() || null,
      advogado_oab: advOab.trim() || null,
      email_presidente: emailPresidente.trim() || null,
      cartorio_id: cartorio?.id ?? null,
      empresa_id: empresa?.id ?? null,
      cliente_id: cliente?.id ?? null,
    });
  };

  return (
    <>
      <Box>
        {isLoading && (
          <Flex justify="center" py={10}>
            <Spinner size="lg" color="blue.500" />
          </Flex>
        )}

        {!isLoading && estatuto && (
          <Stack spacing={5} mt={2}>
            {/* #16: link do formulário só na coluna "Aguardando Validação do Cliente" */}
            {mostrarLink && (
              <Box>
                <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2}>
                  LINK PÚBLICO
                </Text>
                <Flex
                  align="center"
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="lg"
                  px={3}
                  py={2}
                  gap={2}
                >
                  <Text
                    fontSize="11px"
                    color="gray.700"
                    flex={1}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {publicUrl}
                  </Text>
                  <Tooltip label={linkCopied ? 'Copiado!' : 'Copiar link'} fontSize="xs">
                    <IconButton
                      aria-label="Copiar"
                      size="xs"
                      variant="ghost"
                      icon={
                        linkCopied ? (
                          <Check size={14} weight="bold" color="#16A34A" />
                        ) : (
                          <Copy size={14} weight="bold" />
                        )
                      }
                      onClick={handleCopyLink}
                    />
                  </Tooltip>
                </Flex>
              </Box>
            )}

            <SimpleGrid columns={2} spacing={3}>
              <Box>
                <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                  CRIADO EM
                </Text>
                <Text fontSize="xs" color="gray.700">
                  {formatDateTimeBR(estatuto.dt_criacao)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                  EXPIRA EM
                </Text>
                <Text fontSize="xs" color="gray.700">
                  {formatDateTimeBR(estatuto.expires_at)}
                </Text>
              </Box>
              {!alteracao && (
                <>
                  <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                      LINK ENVIADO
                    </Text>
                    <Text fontSize="xs" color="gray.700">
                      {formatDateTimeBR(estatuto.dt_envio_link)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                      PREENCHIDO EM
                    </Text>
                    <Text fontSize="xs" color="gray.700">
                      {formatDateTimeBR(estatuto.dt_preenchimento)}
                    </Text>
                  </Box>
                </>
              )}
            </SimpleGrid>

            {/* T6.1: Aguardando cliente + motivo (bloqueia o processo no cliente) */}
            {estatuto.kanban_card_id != null && !alteracao && (
              <>
                <Divider />
                <Box>
                  <Checkbox
                    colorScheme="orange"
                    isChecked={aguardando}
                    onChange={e => setAguardando(e.target.checked)}
                  >
                    <Text fontSize="sm" fontWeight="600" color="gray.700">
                      Aguardando cliente
                    </Text>
                  </Checkbox>
                  <Text fontSize="xs" color="gray.400" mt={0.5} ml={6}>
                    Bloqueia o processo no cliente e libera ele a mexer/anexar na etapa.
                  </Text>

                  {aguardando && (
                    <FormControl mt={3}>
                      <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                        Motivo do bloqueio{' '}
                        <Text as="span" fontWeight="400" color="gray.400">
                          (opcional)
                        </Text>
                      </FormLabel>
                      <Textarea
                        size="sm"
                        rows={2}
                        placeholder="Contexto extra ao cliente, se quiser (ex.: enviar comprovante de endereço)."
                        value={motivo}
                        onChange={e => setMotivo(e.target.value)}
                      />
                    </FormControl>
                  )}

                  <Button
                    mt={3}
                    size="sm"
                    colorScheme="orange"
                    variant="outline"
                    isDisabled={aguardandoSemMudanca}
                    isLoading={aguardandoMutation.isPending}
                    onClick={() => aguardandoMutation.mutate()}
                  >
                    Salvar
                  </Button>
                </Box>
              </>
            )}

            {estatuto.kanban_card_id != null &&
              (estatuto.status === 'enviar_assinatura' ||
                estatuto.status === 'assinado_finalizado') && (
                <>
                  <Divider />
                  <EstatutoAssinaturaStatus
                    cardId={estatuto.kanban_card_id}
                    estatutoId={estatuto.id}
                    autoSync={estatuto.status !== 'assinado_finalizado'}
                  />
                </>
              )}

            {/* 23/10: a equipe corrige dado de pessoa depois do envio (sem a validação, só ela) */}
            {estatuto.dados_preenchidos &&
              (estatuto.status === 'aguardando_validacao' ||
                estatuto.status === 'enviar_assinatura') && (
                <>
                  <Divider />
                  <EstatutoCorrigirPessoa estatuto={estatuto} />
                </>
              )}

            {/* #14: Cartório + Advogado — editáveis pelo botão de lápis (PATCH) */}
            <>
              <Divider />
              <Flex justify="space-between" align="center">
                <Text fontSize="xs" fontWeight="700" color="gray.500">
                  DADOS DO PROCESSO
                </Text>
                {!editando && (
                  <IconButton
                    aria-label="Editar dados"
                    size="xs"
                    variant="ghost"
                    icon={<PencilSimple size={14} weight="bold" />}
                    onClick={() => setEditando(true)}
                  />
                )}
              </Flex>

              {editando ? (
                <Stack spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                      Empresa (Área VIP)
                    </FormLabel>
                    <EmpresaSelect value={empresa} onChange={setEmpresa} />
                    <Text fontSize="10px" color="gray.400" mt={1}>
                      Vincula o processo à empresa do cliente na Área VIP. Deixe vazio para não
                      vincular.
                    </Text>
                  </FormControl>

                  {/* #25: vínculo opcional a um cliente específico (usuário do portal). */}
                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                      Cliente (opcional)
                    </FormLabel>
                    <ClienteSelect value={cliente} onChange={setCliente} />
                    <Text fontSize="10px" color="gray.400" mt={1}>
                      Vincula o card a um cliente específico do portal. Deixe vazio para não
                      vincular.
                    </Text>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                      Cartório
                    </FormLabel>
                    <CartorioSelect value={cartorio} onChange={setCartorio} />
                  </FormControl>

                  {!alteracao && (
                    <FormControl>
                      <FormLabel fontSize="11px" fontWeight="600" color="gray.500" mb={1}>
                        E-mail do presidente (recebe o link do formulário)
                      </FormLabel>
                      <Input
                        size="sm"
                        type="email"
                        value={emailPresidente}
                        onChange={e => setEmailPresidente(e.target.value)}
                      />
                    </FormControl>
                  )}

                  <Text fontSize="11px" fontWeight="700" color="gray.500" pt={1}>
                    ADVOGADO (2º ASSINANTE)
                  </Text>
                  <SimpleGrid columns={2} spacing={2}>
                    <FormControl>
                      <FormLabel fontSize="10px" color="gray.500" mb={0.5}>
                        Nome
                      </FormLabel>
                      <Input size="sm" value={advNome} onChange={e => setAdvNome(e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="10px" color="gray.500" mb={0.5}>
                        E-mail
                      </FormLabel>
                      <Input
                        size="sm"
                        value={advEmail}
                        onChange={e => setAdvEmail(e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="10px" color="gray.500" mb={0.5}>
                        CPF
                      </FormLabel>
                      <Input size="sm" value={advCpf} onChange={e => setAdvCpf(e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="10px" color="gray.500" mb={0.5}>
                        OAB
                      </FormLabel>
                      <Input size="sm" value={advOab} onChange={e => setAdvOab(e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <Flex gap={2} pt={1}>
                    <Button
                      size="sm"
                      colorScheme="purple"
                      isLoading={editarMutation.isPending}
                      onClick={salvarEdicao}
                    >
                      Salvar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditando(false)}>
                      Cancelar
                    </Button>
                  </Flex>
                </Stack>
              ) : (
                <>
                  <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                      EMPRESA (ÁREA VIP)
                    </Text>
                    {estatuto.empresa_id != null ? (
                      <Text fontSize="sm" color="gray.800" fontWeight="600">
                        {estatuto.empresa_nome || `Empresa #${estatuto.empresa_id}`}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="orange.500" fontWeight="600">
                        Sem empresa vinculada
                      </Text>
                    )}
                  </Box>
                  {/* #25: cliente vinculado (opcional). O backend resolve o nome (cliente_nome);
                      placeholder "Cliente #id" só se o registro não resolver. */}
                  <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                      CLIENTE
                    </Text>
                    {estatuto.cliente_id != null ? (
                      <Text fontSize="sm" color="gray.800" fontWeight="600">
                        {estatuto.cliente_nome || `Cliente #${estatuto.cliente_id}`}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="gray.400">
                        —
                      </Text>
                    )}
                  </Box>
                  <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                      CARTÓRIO
                    </Text>
                    {estatuto.cartorio ? (
                      <>
                        <Text fontSize="sm" color="gray.800" fontWeight="600">
                          {estatuto.cartorio.nome}
                        </Text>
                        {(estatuto.cartorio.telefone || estatuto.cartorio.email) && (
                          <Text fontSize="11px" color="gray.500">
                            {estatuto.cartorio.telefone || ''}
                            {estatuto.cartorio.telefone && estatuto.cartorio.email ? ' · ' : ''}
                            {estatuto.cartorio.email || ''}
                          </Text>
                        )}
                      </>
                    ) : (
                      <Text fontSize="sm" color="gray.400">
                        —
                      </Text>
                    )}
                  </Box>
                  {!alteracao && (
                    <Box>
                      <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                        E-MAIL DO PRESIDENTE
                      </Text>
                      <Text
                        fontSize="sm"
                        color={estatuto.email_presidente ? 'gray.800' : 'gray.400'}
                      >
                        {estatuto.email_presidente || '—'}
                      </Text>
                    </Box>
                  )}
                  <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                      ADVOGADO (2º ASSINANTE)
                    </Text>
                    {estatuto.advogado_nome ? (
                      <>
                        <Text fontSize="sm" color="gray.800" fontWeight="600">
                          {estatuto.advogado_nome}
                        </Text>
                        <Text fontSize="11px" color="gray.500">
                          {estatuto.advogado_email || '—'}
                        </Text>
                        {(estatuto.advogado_cpf || estatuto.advogado_oab) && (
                          <Text fontSize="11px" color="gray.500">
                            {estatuto.advogado_cpf ? `CPF ${estatuto.advogado_cpf}` : ''}
                            {estatuto.advogado_cpf && estatuto.advogado_oab ? ' · ' : ''}
                            {estatuto.advogado_oab ? `OAB ${estatuto.advogado_oab}` : ''}
                          </Text>
                        )}
                      </>
                    ) : (
                      <Text fontSize="sm" color="gray.400">
                        —
                      </Text>
                    )}
                  </Box>
                </>
              )}
            </>

            {/* Responsáveis / Supervisores do processo (Task #496) */}
            {((estatuto.responsaveis && estatuto.responsaveis.length > 0) ||
              (estatuto.supervisores && estatuto.supervisores.length > 0)) && (
              <Box>
                {estatuto.responsaveis && estatuto.responsaveis.length > 0 && (
                  <Box mb={estatuto.supervisores?.length ? 3 : 0}>
                    <Text fontSize="xs" fontWeight="700" color="gray.500" mb={2}>
                      RESPONSÁVEL(EIS)
                    </Text>
                    {estatuto.responsaveis.map(r => (
                      <Text key={r.id} fontSize="sm" color="gray.800" fontWeight="600">
                        {r.nome || '—'}
                      </Text>
                    ))}
                  </Box>
                )}
                {estatuto.supervisores && estatuto.supervisores.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color="purple.500" mb={2}>
                      SUPERVISOR(ES)
                    </Text>
                    {estatuto.supervisores.map(s => (
                      <Text key={s.id} fontSize="sm" color="gray.800" fontWeight="600">
                        {s.nome || '—'}
                      </Text>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Baixa do fluxo novo (origem/convocação); a antiga foi preenchida com o formulário
                da constituição e segue na lista de baixo. */}
            {baixaNova && <EstatutoBaixaDados dados={dados as any} />}

            {dados && !baixaNova && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="xs" fontWeight="700" color="gray.500" mb={3}>
                    DADOS PREENCHIDOS PELO CLIENTE
                  </Text>
                  <Stack spacing={3}>
                    {/* Só o que o cliente mandou (registro antigo não tem os campos novos);
                        "até quando" só vale com o balancete trimestral. */}
                    {(Object.keys(FIELD_LABELS) as (keyof IEstatutoFormPayload)[])
                      .filter(
                        key =>
                          key in dados &&
                          (key !== 'balancete_trimestral_ate' ||
                            dados.periodicidade_balancete === 'trimestral'),
                      )
                      .map(key => (
                        <Box key={key}>
                          <Text fontSize="10px" color="gray.500" fontWeight="600" mb={0.5}>
                            {FIELD_LABELS[key]}
                          </Text>
                          <Text fontSize="xs" color="gray.700" whiteSpace="pre-wrap">
                            {renderValue(key, dados[key], dados)}
                          </Text>
                        </Box>
                      ))}
                  </Stack>
                </Box>
              </>
            )}
          </Stack>
        )}
      </Box>
      {/* Os modais de Assinatura/PDF são renderizados no index (fora da aba condicional),
          pois seu estado vive no hook — evita desmontar ao trocar de aba. */}
    </>
  );
};
